/**
 * useHlsVideo — attach an adaptive HLS stream to a <video> element with
 * automatic MP4 fallback when the transcode hasn't finished yet.
 *
 * Strategy (TikTok-style smart playback):
 *
 *   1. If browser has native HLS (Safari / iOS) → set video.src to the
 *      manifest URL directly.  Native player handles adaptive switching.
 *
 *   2. Otherwise (Chrome/Firefox/Edge) → use hls.js.  Loads master.m3u8,
 *      picks an initial variant by network speed, switches on the fly.
 *
 *   3. If the manifest returns 404 (transcode still running) or hls.js
 *      hits a fatal error → fall back to the plain MP4 /download URL.
 *      This guarantees playback even when HLS isn't ready yet.
 *
 * Returns void; the caller just wires the returned ref via `videoRef`.
 */
import { useEffect } from 'react';
import Hls from 'hls.js';

export interface UseHlsVideoOptions {
  videoRef: React.RefObject<HTMLVideoElement>;
  /** Full HLS master manifest URL (with auth token already in query if needed). */
  manifestUrl: string | null;
  /** Fallback MP4 URL used when HLS is unavailable or errors out. */
  fallbackUrl: string;
  /** Reset trigger — pass something like story.id so the effect re-runs. */
  resetKey?: string;
}

export function useHlsVideo({
  videoRef,
  manifestUrl,
  fallbackUrl,
  resetKey,
}: UseHlsVideoOptions) {
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls: Hls | null = null;
    let aborted = false;

    // ВАЖНО: когда мы меняем `video.src` программно, HTML-атрибут
    // `autoplay` НЕ перезапускается (это по spec).  React ставит <video
    // autoPlay> один раз при монтировании; к моменту, когда src прилетает
    // сюда асинхронно, autoplay уже отстрелялся вхолостую.  Поэтому
    // после любой установки src вызываем v.play() явно, с catch для случая
    // когда autoplay policy браузера блокирует — тогда ждём жеста.
    const tryPlay = (v: HTMLVideoElement) => {
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    async function attach() {
      const v = videoRef.current;
      if (!v || aborted) return;

      // ── 1. If manifest URL wasn't provided, go straight to MP4 ─────
      if (!manifestUrl) {
        v.src = fallbackUrl;
        tryPlay(v);
        return;
      }

      // ── 2. Check manifest actually exists (HEAD) ───────────────────
      // The main backend 302's to the media VPS.  If transcode isn't
      // ready the media VPS returns 404; fall back to MP4.
      try {
        const head = await fetch(manifestUrl, { method: 'HEAD', redirect: 'follow' });
        if (!head.ok) {
          if (!aborted) { v.src = fallbackUrl; tryPlay(v); }
          return;
        }
      } catch {
        if (!aborted) { v.src = fallbackUrl; tryPlay(v); }
        return;
      }
      if (aborted) return;

      // ── 3. Native HLS (Safari/iOS, Chrome 147+) ────────────────────
      if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = manifestUrl;
        tryPlay(v);
        return;
      }

      // ── 4. hls.js for everyone else ────────────────────────────────
      if (Hls.isSupported()) {
        hls = new Hls({
          // TikTok-grade defaults for fast start on mobile.
          lowLatencyMode: false,        // VOD content, not live
          backBufferLength: 30,         // keep 30s backward for rewind
          maxBufferLength: 20,          // don't over-buffer
          maxMaxBufferLength: 30,
          enableWorker: true,
          startLevel: -1,               // auto-pick by network
          autoStartLoad: true,
          capLevelToPlayerSize: true,   // don't fetch 1080p into a 400px <video>
        });
        hls.loadSource(manifestUrl);
        hls.attachMedia(v);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!aborted && videoRef.current) tryPlay(videoRef.current);
        });

        hls.on(Hls.Events.ERROR, (_ev, data) => {
          if (data.fatal) {
            try { hls?.destroy(); } catch { /* noop */ }
            hls = null;
            if (!aborted && videoRef.current) {
              console.warn('[hls] fatal error, falling back to MP4', data);
              videoRef.current.src = fallbackUrl;
              tryPlay(videoRef.current);
            }
          }
        });
        return;
      }

      // ── 5. Last resort — raw MP4 ───────────────────────────────────
      v.src = fallbackUrl;
      tryPlay(v);
    }

    attach();

    return () => {
      aborted = true;
      if (hls) {
        try { hls.destroy(); } catch { /* noop */ }
        hls = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, manifestUrl, fallbackUrl]);
}
